import {AttrsSchema, Component} from 'panel';
import type {Instance as PopperInstance, Placement, VirtualElement} from '@popperjs/core';
import {createPopperLite, flip as popperFlipModifier, offset as popperOffsetModifier} from '@popperjs/core';

import * as colors from 'common/util/colors';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {CHROME_HEADER_HEIGHT} from 'common/constants';

import {POPPER_POSITION_OVERRIDES} from '../drop-menu';
import {Directions, PositionParentType} from '../drop-menu/types';
import {TooltipBulletStyle, TooltipSection} from './types';
import {template} from './template';
import css from './index.inline.styl';

export type ChartTooltipAttrs = {
  sections: TooltipSection[];
  'footer-text'?: string;
  'title-color'?: string;
  direction?: Directions;
  offset?: number;
  skidding?: number;
};

type MousePositioning = {clientX: number; clientY: number};
type ParentPositioning = {positionParent: HTMLElement | PositionParentType.ParentElement};

// can either position automatically relative to an element or absolute mouse coordinates for "follow" behavior
export type ChartTooltipPositioning = ParentPositioning | MousePositioning;

function isParentPositioning(positioning: ChartTooltipPositioning): positioning is ParentPositioning {
  return (
    `positionParent` in positioning &&
    (positioning.positionParent instanceof HTMLElement ||
      positioning.positionParent === PositionParentType.ParentElement)
  );
}

export type ChartTooltipProps = {
  // When provided, the tooltip will automatically position itself relative to this element using popperjs.
  // See mp-drop-menu for details.
  positioning?: ChartTooltipPositioning;
};

export interface ChartTooltipJsxProps {
  attrs?: ChartTooltipAttrs;
  props?: ChartTooltipProps;
}

export class ChartTooltip extends Component<EmptyObj, ChartTooltipAttrs> {
  private _positioning: ChartTooltipPositioning = null;
  protected popperInstance: PopperInstance = null;

  static get attrsSchema(): AttrsSchema<ChartTooltipAttrs> {
    return {
      ...super.attrsSchema,
      'footer-text': {type: `string`},
      sections: {
        type: `json`,
        description: `interface: TooltipSection[]`,
        default: [],
      },
      'title-color': {type: `string`},
      offset: {type: `number`, default: 4},
      skidding: {type: `number`, default: 0, description: `Amount of shifting parallel to parent`},
      direction: {
        description: `Chart tooltip opens on the top of an element if 'up', on the bottom if 'down'`,
        type: `string`,
        enum: Object.values(Directions),
      },
    };
  }

  get config() {
    return {
      template,
      useShadowDom: true,
      css,
    };
  }

  connectedCallback() {
    super.connectedCallback();
    this.setupPopper();
  }

  disconnectedCallback() {
    this.popperInstance?.destroy();
    super.disconnectedCallback();
  }

  set positioning(positioning: ChartTooltipPositioning) {
    this._positioning = positioning;
    if (isParentPositioning(positioning) || !this.popperInstance) {
      this.setupPopper();
    } else {
      this.popperInstance.update();
    }
  }

  get positioning() {
    return this._positioning;
  }

  get createPopper() {
    return createPopperLite;
  }

  /**
   * Setups the positioning library for the dropdown, for more info see:
   * https://popper.js.org/docs/v2/
   */
  setupPopper() {
    this.popperInstance?.destroy();

    if (!this.positioning) {
      return null;
    }

    let positionParent: HTMLElement | VirtualElement;
    if (isParentPositioning(this.positioning)) {
      if (this.positioning.positionParent === PositionParentType.ParentElement) {
        positionParent = this[this.positioning.positionParent];
      } else {
        positionParent = this.positioning.positionParent;
      }
    } else {
      positionParent = {
        getBoundingClientRect: () => {
          const {clientX, clientY} = this.positioning as MousePositioning;
          return new DOMRect(clientX, clientY);
        },
      };
    }

    if (!positionParent) {
      return null;
    }

    this.popperInstance = this.createPopper(positionParent, this);
    const useParentPositioning = isParentPositioning(this.positioning);

    let placement: Placement;
    if (useParentPositioning) {
      placement = this.attr(`direction`) ? POPPER_POSITION_OVERRIDES[this.attr(`direction`)] : `right-start`;
    } else {
      placement = `bottom-start`;
    }

    this.popperInstance.setOptions({
      placement,
      strategy: `fixed`,
      modifiers: [
        {
          ...popperOffsetModifier,
          name: `offset`,
          options: {
            offset: ({placement}) => {
              if (useParentPositioning) {
                return [this.attr(`skidding`), this.attr(`offset`)];
              } else if (placement === `bottom-end`) {
                return [-12, 12];
              } else {
                return [12, 12];
              }
            },
          },
        },
        {
          ...popperFlipModifier,
          name: `flip`,
          data: {
            ...popperFlipModifier.data,
            skipFlip: false,
          },
          options: {
            rootBoundary: `viewport`,
            padding: {top: CHROME_HEADER_HEIGHT},
          },
        },
      ],
    });
  }

  getItemBackground(item) {
    const {bulletStyle, bulletColor} = item;
    return bulletStyle === TooltipBulletStyle.Solid
      ? {background: bulletColor}
      : {
          'background-image': `repeating-linear-gradient(-45deg, ${bulletColor}, ${bulletColor} 25%, ${colors.gray0} 25%, ${colors.gray0} 50%, ${bulletColor} 50%)`,
          'background-size': `4px 4px`,
        };
  }
}

export const ChartTooltipEl = defineAndMakeJsxFactory<ChartTooltipJsxProps>(`mp-chart-tooltip`, ChartTooltip);
export const ChartTooltipReact = panel2react<ChartTooltipJsxProps>(`mp-chart-tooltip`, ChartTooltip);
